import {Button, IconButton, Menu as MuiMenu, Typography} from '@mui/material';
import styled from 'styled-components';
import {media} from '../../../theme';

interface OpenProps {
    open: boolean;
}

export const StyledIconButton = styled(IconButton)`
    img {
        width: 16px;
        height: 11px;
        border: 2px solid ${({theme}) => theme.palette.text.primary};
    }
    
    svg {
        color: ${({theme}) => theme.palette.text.primary};
        width: 24px;
        height: 24px;
    }
`;

export const SidebarHeader = styled.div<{ $isOpen?: boolean }>`
    display: flex;
    justify-content: ${({$isOpen}) => ($isOpen ? 'space-between' : 'center')};
    align-items: center;
    width: 100%;
    height: 28px;
    margin-bottom: 40px;
`;

export const SidebarContent = styled.div<OpenProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
    flex-grow: 1;
    overflow: hidden;
    width: 100%;

    @media (max-width: 959px) {
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: thin;
        scrollbar-color: rgba(29, 42, 65, 0.6) transparent;

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background-color: rgba(29, 42, 65, 0.6);
            border-radius: 10px;
        }
    }
`;

export const OcrBatchContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0;
    flex-grow: 1;
    overflow: hidden;
    width: 100%;
`;

export const OcrBatchHistorySection = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: ${({theme}) => theme.palette.action.hover} transparent;
    width: 100%;
    min-height: 0;
    margin-top: 6px;
    
    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${({theme}) => theme.palette.action.hover};
        border-radius: 10px;
    }

    &::-webkit-scrollbar-button {
        width: 0;
        height: 0;
        display: none;
    }

    &::-webkit-scrollbar-corner {
        background: transparent;
    }

    @media (max-width: 959px) {
        overflow: visible;
    }
`;

export const ConversationHistorySection = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: hidden;
    width: 100%;
    min-height: 0;
    margin-top: 8px;

    @media (max-width: 959px) {
        overflow: visible;
    }
`;

export const LogoBox = styled.div`
    cursor: pointer;
    margin-top: 10px;

    img {
        width: 140px;
    }
`;

// TODO: THIN SCROLLBAR !!!
export const ConversationList = styled.div<OpenProps>`
    width: ${(props) => (props.open ? '240px' : '50px')};
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: ${({theme}) => theme.palette.action.hover} transparent;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: ${({theme}) => theme.palette.action.hover};
        border-radius: 10px;
    }

    &::-webkit-scrollbar-button {
        width: 0;
        height: 0;
        display: none;
    }

    &::-webkit-scrollbar-corner {
        background: transparent;
    }

    @media (max-width: 959px) {
        overflow: visible;
    }
`;

export const StyledSectionHeader = styled(Typography)`
    width: 100%;
    font-weight: 500;
    line-height: 20px;
    margin: 8px 0;
`;

export const HistoryHeader = styled(Typography)`
    padding: 4px 0;
    line-height: 20px;
    width: 100%;
    overflow: visible;
`;

export const ToolsHeader = styled(Typography)`
    line-height: 20px;
    padding: 4px 0;
    width: 100%;
    overflow: visible;
`;

export const ToolsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
`;

export const ToolItem = styled(Button)<{ $active?: boolean; $open?: boolean; $disabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: ${({ $open }) => $open ? 'flex-start' : 'center'};
    
    height: 40px;
    width: ${({ $open }) => $open ? '100%' : '40px'};
    min-width: unset;
    padding: ${({ $open }) => $open ? '8px 12px' : '0'};

    gap: ${({ $open }) => $open ? '6px' : '0'};

    transition: all 200ms ease;
    border-radius: ${({ $open }) => $open ? '20px' : '50%'};

    opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
    pointer-events: ${({ $disabled }) => $disabled ? 'none' : 'auto'};
    cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};

    font-weight: 400;
    background: ${({ $active, theme }) =>
        $active ? theme.palette.primary.main : 'transparent'};

    &:hover {
        background: ${({ $active, theme }) =>
                            $active ? theme.palette.primary.dark : theme.palette.action.hover};
    }

    svg, img {
        display: block;
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        margin-right: ${({ $open }) => $open ? '6px' : '0'};
        color: ${({ $active, theme }) =>
                            $active ? theme.palette.primary.contrastText : theme.palette.primary.main};
    }

    span {
        display: ${({ $open }) => $open ? 'inline' : 'none'};
        color: ${({ $active, theme }) =>
                            $active ? theme.palette.primary.contrastText : theme.palette.primary.main};
        line-height: 20px;
        font-size: ${({ theme }) => theme.typography.body1.fontSize};
        text-overflow: ellipsis;
        white-space: nowrap;
        letter-spacing: ${({ theme }) => theme.typography.body1.letterSpacing};
    }
`;

export const StyledMenu = styled(MuiMenu)`
    .MuiPaper-root {
        border-radius: 20px;
        border: 1px solid ${({theme}) => theme.palette.dividers.subtle};
    }

    .MuiMenuItem-root {
        font-size: ${({theme}) => theme.typography.body2.fontSize};
    }
`;

export const MobileMenuButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    
    position: fixed;
    top: 12px;
    left: 36px;
    z-index: 1300;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${({theme}) => theme.palette.background.default};
    cursor: pointer;
    transition: all 0.2s ease;

    border: 1px solid ${({theme}) => theme.palette.dividers.default};
    
    &:hover {
        background: ${({theme}) => theme.palette.action.hover};
    }

    &:active {
        transform: scale(0.95);
    }

    svg {
        width: 24px;
        height: 24px;
        color: ${({theme}) => theme.palette.text.primary};
    }

    ${media.md} {
        display: none;
    }
`;